import { useState, type FormEvent } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { settingsApi } from '@/services/settings';
import { ApiClientError } from '@/services/api';

export interface SmsMfaDisableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  phoneLastFour: string | null;
}

/**
 * Modal for disabling SMS MFA
 * Requires TOTP code verification (not SMS code) for SIM swap protection
 */
export default function SmsMfaDisableModal({
  isOpen,
  onClose,
  onSuccess,
  phoneLastFour,
}: SmsMfaDisableModalProps) {
  const [totpCode, setTotpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (totpCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await settingsApi.disableSmsMfa({ totpCode });
      onSuccess();
      handleClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else {
        setError('Failed to disable SMS MFA. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setTotpCode('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sms-mfa-disable-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h2 id="sms-mfa-disable-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
          Disable SMS Backup
        </h2>

        <Alert variant="warning" className="mb-4">
          Disabling SMS backup will remove your phone number as a fallback authentication method.
        </Alert>

        {phoneLastFour && (
          <p className="text-sm text-gray-500 mb-4">
            Current phone: ***-***-{phoneLastFour}
          </p>
        )}

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-gray-600">
            Enter your authenticator app code to confirm. This protects against
            SIM swap attacks.
          </p>

          <Input
            label="Authenticator Code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            autoComplete="one-time-code"
            autoFocus
            disabled={isLoading}
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={totpCode.length !== 6}
              isLoading={isLoading}
              className="flex-1"
            >
              Disable SMS
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
